define((require) => {
    "use strict";

    const {delay}              = require("underscore");
    const ADSignInView         = require("ui/user/ADSignInView");
    const Browser              = require("shared/services/Browser");
    const EmailUtil            = require("util/EmailUtil");
    const ImprovedView         = require("editor/zbone/ImprovedView");
    const SignInViewController = require("ui/SignInViewController");
    const {isBlank}            = require("shared/util/StringUtils");
    const template             = require("ui/user/SignInView.tpl");
    const ValidateBehavior     = require("ui/behaviors/ValidateBehavior");
    const Configuration        = require("shared/services/Configuration");

    const AUTH0_DOMAIN = Configuration.auth0Domain;
    const AUTH0_CLIENT_ID = Configuration.auth0ClientId;
    const AUTH0_AUDIENCE = Configuration.auth0Audience;

    const SHAKE_EFFECT_CLASS_NAME = "shake",
        TERMS_OF_USE_URL = "http://community.zmags.com/articles/Knowledgebase/Creator-Product-Terms-of-Use";

    // View->Controller events...
    const Events = {
        SUBMIT: "submit"
    };

    /**
     * The sign in view.
     *
     * @class SignInView
     * @constructor
     *
     * @cfg {Deferred} loginDeferred Deferred to resolve when the user has successfully logged in.
     * @cfg {boolean} [adLogin] Whether to show the Active Directory login form immediately.
     */
    return ImprovedView.extend({

        /**
         * @private @type {Boolean}
         * Flag to toggle AD login on/off.
         */
        _adView: false,

        template,

        templateHelpers: {
            browser: Browser,
            termsOfUseURL: TERMS_OF_USE_URL
        },

        controller: SignInViewController,

        className: "sign-in-page pt-page",

        transitions: {
            out: "pt-page-scaleDown"
        },

        ui: {
            signInView:      ".sign-in-view",
            logo:            ".sign-in-logo",
            form:            ".login-form",
            emailInput:      ".email-input",
            passwordInput:   ".password-input",
            signInButton:    ".sign-in-button",
            signInError:     ".sign-in-error",
            ssoButton:       ".sso-button",
            innerContainer:  ".inner-container",
            persistentInput: ".persistent-input",
            termsOfUse:      ".terms-of-use"
        },

        events: {
            "submit @ui.form": "_loginSubmitHandler",
            "click .sso-button": "_ssoRedirect"
        },

        regions: {
            activeDirectory: ".active-directory",
            throttleRegion:  ".throttle-region",
            defaultFooterRegion: ".default-footer"
        },

        keyboard: {
            "alt-shift-y": "_toggleAdSignInView"
        },
        behaviors: {
            validateBehavior: {
                behaviorClass: ValidateBehavior,
                inputValidation: {
                    ".email-input": {
                        validator(email) {
                            return   this._adView || EmailUtil.validateEmail(email);
                        },
                        errorText(email) {
                            if (!email) {
                                return "Email required";
                            } else if (!EmailUtil.validateEmail(email)) {
                                return "Not a valid email";
                            }

                            return "";
                        }
                    },
                    ".password-input": {
                        validator(password) {
                            return  this._adView || !isBlank(password);
                        },
                        errorText: "Password required"
                    }
                }
            }
        },

        _loginSubmitHandler(event) {
            // Stop form from submitting normally
            event.preventDefault();

            // Signal controller that form was submitted...
            this.trigger(Events.SUBMIT);

            return false;
        },

        _ssoRedirect() {
            const redirectUri = window.location.origin; // redirect back to the origin after sign-in with Auth0

            /*
            This /authorize URL initiates login to the ZMags Auth0 tenant for the specified app, and has these params:
              - response_type: indicates whether the response should be a JWT (value = token) or an authorization code
              - client_id: corresponds to the application configuration in Auth0 that should be used for sign-in
              - redirect_uri: where the user should be redirected after login (to handle the JWT)
              - audience: identifies the expected aud claim in the JWT, and uses the corresponding signing config in Auth0
            */
            window.location.href = `${AUTH0_DOMAIN}/authorize?response_type=token&client_id=${AUTH0_CLIENT_ID}&redirect_uri=${redirectUri}&audience=${AUTH0_AUDIENCE}&prompt=login`;
        },

        _toggleAdSignInView() {
            if (this._adView) {
                this.ui.form.show();
                this.activeDirectory.reset();
            } else {
                this.ui.form.hide(); // throttle view inside this form!
                this.activeDirectory.show(new ADSignInView({
                    username: this.ui.emailInput.val(),
                    loginDeferred: this.options.loginDeferred
                }));
            }
            this._adView = !this._adView;
        },

        /**
         * Applies the "shake" effect to this view.
         *
         * @param {Boolean} [enable] Optional boolean value, if <tt>false</tt> the effect will be removed.
         */
        shake(enable) {
            if (typeof enable === "undefined" || enable === true) {
                this.ui.form.addClass(SHAKE_EFFECT_CLASS_NAME);
            } else {
                this.ui.form.removeClass(SHAKE_EFFECT_CLASS_NAME);
            }
        },

        /**
         * Show/hides terms-of-use from this view. <p>
         *
         * @param {Boolean} show True to show (if not already), false to hide.
         */
        showTermsOfUse(show) {
            if (show) {
                this.ui.termsOfUse.show();
            } else {
                this.ui.termsOfUse.hide();
            }
        },

        /**
         * Show/hide a message with sign-in error details and remediation steps. <p>
         *
         * @param {Boolean} show True to show (if not already), false to hide.
         */
        showErrorMessage(message) {
            if (message) {
                this.ui.signInError.html(message);
                this.ui.signInError.show();
            } else {
                this.ui.signInError.hide();
            }
        },

        /**
         * Fade in logo and login form.
         */
        _fadeElementsIn() {
            /**
             * The elements are born with the "show" class (ie. faded in), just so users
             * don't get a blank page in case this function fails. First we remove .show
             * to hide them...
             */
            this.ui.logo.removeClass("show");
            this.ui.innerContainer.removeClass("show");

            /**
             * ... then we fade them back in at different intervals (and speed, see css).
             */
            delay(() => {
                this.ui.logo.addClass("show");
            }, 1);

            delay(() => {
                if (this.ui.innerContainer) {
                    this.ui.innerContainer.addClass("show");
                    // Give password focus if we already have a username, i.e. on soft login for read only tokens...
                    if (this.ui.emailInput.val()) {
                        this.ui.passwordInput.focus();
                    } else {
                        this.ui.emailInput.focus();
                    }
                }
            }, 1000);
        },

        onRender() {
            if (this.options.adLogin) {
                this._toggleAdSignInView();
            }
            // If we have a username set, make it fixed, e.g. for re-login. Does not make sense to allow the
            // user to enter a new username (even though the frontend supports it)...
            if (this.options.username) {
                this.ui.emailInput.val(this.options.username);
                this.ui.emailInput.attr("disabled", true);
            }
            this._fadeElementsIn();
        }

    }, {
        Events: Events
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/user/SignInView.js